import { css } from '@emotion/core';
import { Button, Form as AntdForm, Input, message, Switch } from 'antd';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormItem, RoleRadioGroup, TypeRadioGroup } from '@/components';
import api from '@/apis';
import { GROUP_ALLOW_APPLY_TYPE, TEAM_PERMISSION } from '@/constants';
import { FC, UserTeam } from '@/interfaces';
import { AppState } from '@/store';
import { editTeam, setCurrentTeam } from '@/store/team/slice';
import { toLowerCamelCase } from '@/utils';
import { TEAM_NAME_REGEX } from '@/utils/regex';
import { can } from '@/utils/user';

/** 修改团队表单的属性接口 */
interface TeamEditFormProps {
  className?: string;
}
/**
 * 修改团队表单
 * 从 redux 的 currentTeam 中读取值，使用前必须先
 * dispatch(setCurrentTeam({ id }));
 */
export const TeamEditForm: FC<TeamEditFormProps> = ({ className }) => {
  const { formatMessage } = useIntl(); // i18n
  const [form] = AntdForm.useForm();
  const dispatch = useDispatch();
  const currentTeam = useSelector(
    (state: AppState) => state.team.currentTeam,
  ) as UserTeam;
  const [submitting, setSubmitting] = useState(false);
  const [isAllowApply, setIsAllowApply] = useState(
    currentTeam.allowApplyType !== GROUP_ALLOW_APPLY_TYPE.NONE,
  );
  const [isRobotWebhookEnabled, setIsRobotWebhookEnabled] = useState(
    Boolean(currentTeam.robotWebhookEnabled),
  );

  // 团队数据刷新或保存成功后，回填表单。
  useEffect(() => {
    form.setFieldsValue(toLowerCamelCase(currentTeam));
    setIsRobotWebhookEnabled(Boolean(currentTeam.robotWebhookEnabled));
  }, [currentTeam, form]);

  const handleFinish = (values: any) => {
    const data = { ...values };
    if (!data.robotWebhookAuthToken) {
      delete data.robotWebhookAuthToken;
    }
    setSubmitting(true);
    api
      .editTeam({ id: currentTeam.id, data })
      .then((result) => {
        const responseData = toLowerCamelCase(result.data);
        // 修改成功
        dispatch(editTeam(responseData.team));
        dispatch(setCurrentTeam(responseData.team));
        // 弹出提示
        message.success(responseData.message);
      })
      .catch((error) => {
        error.default(form);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div
      className={className}
      css={css`
        width: 100%;
        .ant-form-item:last-child {
          margin-bottom: 0;
        }
      `}
    >
      <Form
        form={form}
        onFinish={handleFinish}
        initialValues={{ intro: '' }}
        hideRequiredMark
        onValuesChange={(values) => {
          // 关闭加入时，隐藏加入选项
          if (values.allowApplyType) {
            setIsAllowApply(
              values.allowApplyType !== GROUP_ALLOW_APPLY_TYPE.NONE,
            );
          }
          if (typeof values.robotWebhookEnabled !== 'undefined') {
            setIsRobotWebhookEnabled(values.robotWebhookEnabled);
          }
        }}
      >
        <FormItem
          name="name"
          label={formatMessage({ id: 'team.name' })}
          rules={[
            {
              pattern: TEAM_NAME_REGEX,
              message: formatMessage({ id: 'auth.userNameFormatTip' }),
            },
            { required: true },
            { min: 2 },
            { max: 18 },
          ]}
        >
          <Input disabled={!can(currentTeam, TEAM_PERMISSION.CHANGE)} />
        </FormItem>
        <FormItem
          name="intro"
          label={formatMessage({ id: 'team.intro' })}
          rules={[{ min: 0 }, { max: 140 }]}
        >
          <Input.TextArea
            disabled={!can(currentTeam, TEAM_PERMISSION.CHANGE)}
          />
        </FormItem>
        <FormItem
          name="allowApplyType"
          label={formatMessage({ id: 'site.allowApplyTypeLabel' })}
          rules={[
            {
              required: true,
              message: formatMessage({ id: 'form.selectRequired' }),
            },
          ]}
        >
          <TypeRadioGroup
            typeName="allowApplyType"
            groupType="team"
            disabled={!can(currentTeam, TEAM_PERMISSION.CHANGE)}
          />
        </FormItem>
        <FormItem
          style={{
            display: isAllowApply ? 'flex' : 'none',
          }}
          name="applicationCheckType"
          label={formatMessage({ id: 'site.applicationCheckTypeLabel' })}
          rules={[
            {
              required: true,
              message: formatMessage({ id: 'form.selectRequired' }),
            },
          ]}
        >
          <TypeRadioGroup
            typeName="applicationCheckType"
            groupType="team"
            disabled={!can(currentTeam, TEAM_PERMISSION.CHANGE)}
          />
        </FormItem>
        <FormItem
          name="robotWebhookEnabled"
          label={formatMessage({ id: 'team.robotWebhookEnabled' })}
          valuePropName="checked"
        >
          <Switch disabled={!can(currentTeam, TEAM_PERMISSION.CHANGE)} />
        </FormItem>
        <FormItem
          style={{ display: isRobotWebhookEnabled ? 'flex' : 'none' }}
          name="robotWebhookUrl"
          label={formatMessage({ id: 'team.robotServiceUrl' })}
          rules={[
            {
              required: isRobotWebhookEnabled,
              message: formatMessage({ id: 'team.robotServiceUrl.required' }),
            },
            {
              type: 'url',
              message: formatMessage({ id: 'team.robotServiceUrl.invalidUrl' }),
            },
          ]}
        >
          <Input
            disabled={!can(currentTeam, TEAM_PERMISSION.CHANGE)}
            placeholder={formatMessage({ id: 'team.robotServiceUrl.placeholder' })}
          />
        </FormItem>
        <FormItem
          style={{ display: isRobotWebhookEnabled ? 'flex' : 'none' }}
          name="robotWebhookAuthToken"
          label={formatMessage({ id: 'team.robotWebhookAuthToken' })}
          rules={[
            {
              required: isRobotWebhookEnabled,
              message: formatMessage({ id: 'team.robotWebhookAuthToken.required' }),
            },
          ]}
        >
          <Input.Password
            disabled={!can(currentTeam, TEAM_PERMISSION.CHANGE)}
            placeholder={formatMessage({ id: 'team.robotWebhookAuthToken.placeholder' })}
          />
        </FormItem>
        <FormItem
          style={{ display: isRobotWebhookEnabled ? 'flex' : 'none' }}
          name="robotWebhookGroupID"
          label={formatMessage({ id: 'team.robotWebhookGroupId' })}
          rules={[
            {
              required: isRobotWebhookEnabled,
              message: formatMessage({ id: 'team.robotWebhookGroupId.required' }),
            },
            {
              pattern: /^\d+$/,
              message: formatMessage({ id: 'team.robotWebhookGroupId.invalid' }),
            },
          ]}
        >
          <Input
            disabled={!can(currentTeam, TEAM_PERMISSION.CHANGE)}
            inputMode="numeric"
            placeholder={formatMessage({
              id: 'team.robotWebhookGroupId.placeholder',
            })}
          />
        </FormItem>
        <FormItem
          style={{
            display: isAllowApply ? 'flex' : 'none',
          }}
          name="defaultRole"
          label={formatMessage({ id: 'site.defaultRoleLabel' })}
          rules={[
            {
              required: true,
              message: formatMessage({ id: 'form.selectRequired' }),
            },
          ]}
        >
          <RoleRadioGroup
            groupType="team"
            disabled={!can(currentTeam, TEAM_PERMISSION.CHANGE)}
          />
        </FormItem>
        {can(currentTeam, TEAM_PERMISSION.CHANGE) && (
          <FormItem>
            <Button type="primary" block htmlType="submit" loading={submitting}>
              {formatMessage({ id: 'site.save' })}
            </Button>
          </FormItem>
        )}
      </Form>
    </div>
  );
};
